<template>
    <div
        class="flex relative"
        ref="wrapper"
    >
        <div class="relative w-full">
            <label
                class="select-filter select-filter-left"
                :class="{
                    'select-filter-active': active,
                }"
                @click="open"
            >
                <p class="label label-small">
                    Check in
                    <span
                        v-if="showOptional"
                        class="text-brand-2-950/60"
                    >
                        (Optional)
                    </span>
                </p>

                <p class="select-filter-note">
                    <!-- pendingStart is the first tap of a range, before the
                         second completes it. Shown because on a touch device
                         nothing else acknowledges that tap at all. -->
                    <span v-if="dates.startDate || pendingStart">{{ formatUkDate(dates.startDate || pendingStart) }}</span>
                    <template v-else>
                        Select date
                    </template>
                </p>

                <input
                    type="hidden"
                    name="start_date"
                    v-model="dates.startDate"
                >
            </label>

            <DeleteButton
                v-if="dates.startDate"
                class="absolute top-1/2 right-3 xl:right-5 -translate-y-1/2"
                @click.prevent="clearStartDate"
            />
        </div>

        <div class="relative w-full">
            <label
                class="select-filter select-filter-right"
                :class="{
                    'select-filter-active': active,
                }"
                @click="open"
            >
                <p class="label label-small">
                    Check out
                    <span
                        v-if="showOptional"
                        class="text-brand-2-950/60"
                    >
                        (Optional)
                    </span>
                </p>

                <p class="select-filter-note">
                    <span v-if="dates.endDate">{{ formatUkDate(dates.endDate) }}</span>
                    <!-- Once an arrival is picked this box said "Select date"
                         again - the same words as before anything happened, so
                         nothing acknowledged progress or said what to do next. -->
                    <template v-else-if="pendingStart">
                        Now pick this one
                    </template>
                    <template v-else>
                        Select date
                    </template>
                </p>

                <input
                    type="hidden"
                    name="end_date"
                    v-model="dates.endDate"
                />
            </label>

            <DeleteButton
                v-if="dates.endDate"
                class="absolute top-1/2 right-3 xl:right-5 -translate-y-1/2"
                @click.prevent="clearEndDate"
            />
        </div>

        <Transition>
            <Dropdown
                title="Dates"
                class="w-full lg:w-[586px] xl:w-[720px]"
                v-model:active="active"
                @close="active = false"
            >
                <div :class="{ 'opacity-60 pointer-events-none': loading }">
                    <!-- Reserves the calendar's rough height for the beat between
                         first open and the async chunk arriving. -->
                    <div
                        v-if="!everOpened"
                        class="min-h-[280px]"
                    ></div>
                    <!-- The two-step instruction. flatpickr's only cue that a
                         second date is expected is the hover preview after the
                         first click, and there is no hover on a touch device -
                         so on a phone or tablet this line is the ONLY thing
                         that says so. It tracks state rather than sitting
                         static, because "now pick the second one" is the half
                         that people actually need - and the third state below
                         exists because a static instruction, sitting above a
                         range the guest has already chosen, reads as though
                         nothing had been chosen at all.

                         "Arrival" and "departure", not "the day you arrive" and
                         "the day you leave": the boxes above are labelled Check
                         in and Check out, so the line under them should be in
                         the same register rather than a chattier paraphrase of
                         it. The confirmations keep the VERB, though - "Arriving
                         Sat 26 Sept" is a sentence about this guest's stay,
                         where "Arrival Sat 26 Sept" is a field in a table. -->
                    <p class="text-xsss sm:text-xs text-brand-2-950/70 mb-3">
                        <template v-if="pendingStart">
                            Arriving {{ formatUkDate(pendingStart) }} &ndash; now pick the departure date.
                        </template>
                        <template v-else-if="dates.startDate && dates.endDate">
                            Arriving {{ formatUkDate(dates.startDate) }}, leaving {{ formatUkDate(dates.endDate) }}. Pick a new arrival date to start again.
                        </template>
                        <template v-else>
                            Pick the arrival date, then the departure date.
                        </template>
                    </p>

                    <Datepicker
                        v-if="everOpened"
                        :key="componentKey"
                        minDate='today'
                        :inline="true"
                        :availableDates="availableDates"
                        v-model:defaultDate="defaultDate"
                        v-model:startDate="dates.startDate"
                        v-model:endDate="dates.endDate"
                        @update:partial-range="onPartialRange"
                    />

                    <!-- Same key as the Availability calendar: only booked days
                         are dotted, so it's only shown when availability data
                         exists (the same condition that draws the dots). -->
                    <p
                        v-if="availableDates && availableDates.length"
                        class="mt-4 inline-flex items-center gap-1.5 text-xsss sm:text-xs leading-none"
                    >
                        <span class="size-3 rounded-full bg-amber-500 -mt-1 relative"></span>
                        Booked
                    </p>
                </div>
            </Dropdown>
        </Transition>
    </div>
</template>

<script setup>
    import { ref, onMounted, useTemplateRef, watch, defineAsyncComponent } from 'vue';

    import DeleteButton from '@js/src/components/DeleteButton.vue';
    import Dropdown from '@js/src/components/Dropdown.vue';

    // Async on purpose: Dropdown hides its content with v-show, so a static
    // import meant flatpickr downloaded AND built a two-month calendar at page
    // load for a picker nobody had opened - on every property page and every
    // archive. The chunk now loads on the first open (everOpened, sticky so
    // state survives close/reopen exactly as it did with v-show alone).
    const Datepicker = defineAsyncComponent(() => import('@js/src/components/Datepicker.vue'));

    import { formatUkDate } from '@js/src/stores/enquiryDates';

    const emit = defineEmits(['startChanged', 'endChanged', 'datesSelected']);

    const props = defineProps([
        'loading',
        'startDate',
        'endDate',
        'showOptional',
        // Y-m-d dates the property is available; unavailable days are visually
        // marked (not disabled) inside the popover.
        'availableDates',
    ]);

    const wrapper = useTemplateRef('wrapper');

    const active = ref(false);

    const dates = ref({
        startDate: props.startDate ? props.startDate : null,
        endDate: props.endDate ? props.endDate : null,
    });

    const defaultDate = ref([dates.value.startDate, dates.value.endDate]);

    // The first date of a range that has no second date yet. It is NOT a
    // selection - it never reaches `dates`, the hidden inputs or the parent -
    // it exists only so the UI can acknowledge the tap. Without it the first
    // tap of a range changes nothing a touch user can see, because flatpickr's
    // only cue is the hover preview. A guest emailed on 17 Sep 2026 to say the
    // form "doesn't permit the entry of a start and a leaving date, it's just
    // one date", having tapped once and concluded the calendar was broken.
    const pendingStart = ref(null);

    function onPartialRange({ startDate, endDate }) {
        // Once both ends exist the range is a real selection and the boxes
        // show it themselves, so the pending state stands down.
        pendingStart.value = endDate ? null : startDate;
    }

    const componentKey = ref(0);

    const forceRerender = () => {
        componentKey.value += 1;
    };

    watch(dates, (updatedDates) => {
        if ((updatedDates.startDate && !updatedDates.endDate) || (!updatedDates.startDate && updatedDates.endDate)) {
            dates.value = {
                startDate: null,
                endDate: null,
            };
        }

        if ((updatedDates.startDate && updatedDates.endDate) || (!updatedDates.startDate && !updatedDates.endDate)) {
            emit('datesSelected', {
                startDate: updatedDates.startDate,
                endDate: updatedDates.endDate,
            });
        }

        if (updatedDates.startDate && updatedDates.endDate) {
            pendingStart.value = null;
            active.value = false;
        }
    }, {
        deep: true,
    });

    // Reflect selection changes that originate elsewhere (the shared store being
    // updated by the Availability calendar) into the pill text + popover. Guarded
    // so a value we already hold is a no-op — combined with Vue only re-running
    // this when the props actually change, that prevents a feedback loop with the
    // datesSelected emit above.
    watch(() => [props.startDate, props.endDate], ([s, e]) => {
        const ns = s || null;
        const ne = e || null;

        if (ns === dates.value.startDate && ne === dates.value.endDate) {
            return;
        }

        dates.value = { startDate: ns, endDate: ne };
        defaultDate.value = [ns, ne];
        pendingStart.value = null;
    });

    const everOpened = ref(false);

    // Opens only, never closes. Both boxes are labels wired to this, so a
    // toggle meant that tapping "Check out" - the obvious thing to do after
    // picking an arrival - shut the calendar and threw the half range away.
    // Closing stays with the Dropdown's own close button and the outside-click
    // handler below, which are the two places a person means it.
    function open() {
        active.value = true;
        everOpened.value = true;
    }

    function clearStartDate() {
        dates.value.startDate = null;
        pendingStart.value = null;

        forceRerender();
    }

    function clearEndDate() {
        dates.value.endDate = null;
        pendingStart.value = null;

        forceRerender();
    }

    onMounted(() => {
        window.addEventListener('click', function (e) {
            if (wrapper.value && !wrapper.value.contains(e.target)) {
                active.value = false;
            }
        });
    });
</script>
